<template>
  <aside
    v-if="isMenuReady"
    class="new-sidebar-menu"
    :class="{ 'new-sidebar-menu--collapsed': isCollapsed }"
    @mouseleave="closeFlyout"
  >
    <nav class="new-sidebar-menu__navigation">
      <ul class="new-sidebar-menu__list">
        <li
          v-for="(entry, entryIndex) in menu"
          :key="entryKey(entry, entryIndex)"
          class="new-sidebar-menu__item"
          @mouseenter="showFlyout($event, entry)"
        >
          <button
            v-if="entry.child?.length || entry.callback"
            type="button"
            class="new-sidebar-menu__entry new-sidebar-menu__entry--level-1"
            :class="[entry.class, { 'new-sidebar-menu__entry--active': isEntryActive(entry, 1) }]"
            :aria-expanded="entry.child?.length ? isEntryOpen(entry, entryIndex) : undefined"
            @click="activateEntry($event, entry, entryIndex)"
          >
            <i :class="entry.icon" class="new-sidebar-menu__icon" aria-hidden="true" />
            <span class="new-sidebar-menu__label" :title="entry.title">{{ entry.title }}</span>
            <LISABadge
              v-if="entry.badge"
              class="new-sidebar-menu__badge"
              :class="{
                'new-sidebar-menu__badge--notification': entry.badge.notification,
              }"
              :variant="entry.badge.variant ?? 'neutral'"
            >
              {{ entry.badge.text }}
            </LISABadge>
            <i
              v-if="entry.child?.length"
              class="fas fa-chevron-right new-sidebar-menu__arrow"
              :class="{
                'new-sidebar-menu__arrow--open': !isCollapsed && isEntryOpen(entry, entryIndex),
              }"
              aria-hidden="true"
            />
          </button>
          <a
            v-else-if="entry.external"
            class="new-sidebar-menu__entry new-sidebar-menu__entry--level-1"
            :class="[entry.class, { 'new-sidebar-menu__entry--active': isEntryActive(entry, 1) }]"
            :href="entry.href"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i :class="entry.icon" class="new-sidebar-menu__icon" aria-hidden="true" />
            <span class="new-sidebar-menu__label" :title="entry.title">{{ entry.title }}</span>
            <LISABadge
              v-if="entry.badge"
              class="new-sidebar-menu__badge"
              :class="{
                'new-sidebar-menu__badge--notification': entry.badge.notification,
              }"
              :variant="entry.badge.variant ?? 'neutral'"
            >
              {{ entry.badge.text }}
            </LISABadge>
          </a>
          <RouterLink
            v-else
            class="new-sidebar-menu__entry new-sidebar-menu__entry--level-1"
            :class="[entry.class, { 'new-sidebar-menu__entry--active': isEntryActive(entry, 1) }]"
            :to="entryDestination(entry)"
          >
            <i :class="entry.icon" class="new-sidebar-menu__icon" aria-hidden="true" />
            <span class="new-sidebar-menu__label" :title="entry.title">{{ entry.title }}</span>
            <LISABadge
              v-if="entry.badge"
              class="new-sidebar-menu__badge"
              :class="{
                'new-sidebar-menu__badge--notification': entry.badge.notification,
              }"
              :variant="entry.badge.variant ?? 'neutral'"
            >
              {{ entry.badge.text }}
            </LISABadge>
          </RouterLink>

          <ul
            v-if="!isCollapsed && entry.child?.length && isEntryOpen(entry, entryIndex)"
            class="new-sidebar-menu__submenu"
          >
            <li
              v-for="(levelTwo, levelTwoIndex) in entry.child"
              :key="entryKey(levelTwo, levelTwoIndex, entryKey(entry, entryIndex))"
              class="new-sidebar-menu__submenu-item"
            >
              <button
                v-if="levelTwo.child?.length"
                type="button"
                class="new-sidebar-menu__entry new-sidebar-menu__entry--level-2"
                :class="[
                  levelTwo.class,
                  { 'new-sidebar-menu__entry--active': isEntryActive(levelTwo, 2) },
                ]"
                :aria-expanded="isEntryOpen(levelTwo, levelTwoIndex, entryKey(entry, entryIndex))"
                @click="
                  activateIntermediateEntry(
                    entryKey(levelTwo, levelTwoIndex, entryKey(entry, entryIndex)),
                  )
                "
              >
                <span class="new-sidebar-menu__label" :title="levelTwo.title">
                  {{ levelTwo.title }}
                </span>
                <i
                  class="fas fa-chevron-right new-sidebar-menu__arrow"
                  :class="{
                    'new-sidebar-menu__arrow--open': isEntryOpen(
                      levelTwo,
                      levelTwoIndex,
                      entryKey(entry, entryIndex),
                    ),
                  }"
                  aria-hidden="true"
                />
              </button>
              <RouterLink
                v-else
                class="new-sidebar-menu__entry new-sidebar-menu__entry--level-2"
                :class="[
                  levelTwo.class,
                  { 'new-sidebar-menu__entry--active': isEntryActive(levelTwo, 2) },
                ]"
                :to="entryDestination(levelTwo)"
              >
                <span class="new-sidebar-menu__label" :title="levelTwo.title">
                  {{ levelTwo.title }}
                </span>
                <LISABadge
                  v-if="levelTwo.badge"
                  class="new-sidebar-menu__badge"
                  :variant="levelTwo.badge.variant ?? 'neutral'"
                >
                  {{ levelTwo.badge.text }}
                </LISABadge>
              </RouterLink>

              <ul
                v-if="
                  levelTwo.child?.length &&
                  isEntryOpen(levelTwo, levelTwoIndex, entryKey(entry, entryIndex))
                "
                class="new-sidebar-menu__submenu new-sidebar-menu__submenu--level-3"
              >
                <li
                  v-for="(levelThree, levelThreeIndex) in levelTwo.child"
                  :key="
                    entryKey(
                      levelThree,
                      levelThreeIndex,
                      entryKey(levelTwo, levelTwoIndex, entryKey(entry, entryIndex)),
                    )
                  "
                >
                  <RouterLink
                    class="new-sidebar-menu__entry new-sidebar-menu__entry--level-3"
                    :class="[
                      levelThree.class,
                      { 'new-sidebar-menu__entry--active': isEntryActive(levelThree, 3) },
                    ]"
                    :to="entryDestination(levelThree)"
                  >
                    <span class="new-sidebar-menu__label" :title="levelThree.title">
                      {{ levelThree.title }}
                    </span>
                    <LISABadge
                      v-if="levelThree.badge"
                      class="new-sidebar-menu__badge"
                      :variant="levelThree.badge.variant ?? 'neutral'"
                    >
                      {{ levelThree.badge.text }}
                    </LISABadge>
                  </RouterLink>
                </li>
              </ul>
            </li>
          </ul>
        </li>
      </ul>
    </nav>

    <button
      type="button"
      class="new-sidebar-menu__toggle"
      :aria-label="isCollapsed ? t('global.next') : t('global.back')"
      @click="toggleSidebar"
    >
      <span class="new-sidebar-menu__bug-space" aria-hidden="true" />
      <i
        class="fas new-sidebar-menu__collapse-icon"
        :class="isCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'"
        aria-hidden="true"
      />
    </button>

    <section
      v-if="isCollapsed && flyoutEntry?.child?.length"
      class="new-sidebar-menu__flyout"
      :style="{
        top: flyoutBottom == null ? `${flyoutTop}px` : undefined,
        bottom: flyoutBottom == null ? undefined : `${flyoutBottom}px`,
        maxHeight: `${flyoutMaxHeight}px`,
        maxWidth: `${flyoutMaxWidth}px`,
      }"
    >
      <RouterLink
        class="new-sidebar-menu__flyout-title"
        :to="entryDestination(flyoutEntry)"
        @click="closeFlyout"
      >
        <span class="new-sidebar-menu__label">{{ flyoutEntry.title }}</span>
        <LISABadge
          v-if="flyoutEntry.badge"
          class="new-sidebar-menu__badge"
          :variant="flyoutEntry.badge.variant ?? 'neutral'"
        >
          {{ flyoutEntry.badge.text }}
        </LISABadge>
      </RouterLink>
      <template v-for="(levelTwo, levelTwoIndex) in flyoutEntry.child" :key="levelTwoIndex">
        <RouterLink
          v-if="!levelTwo.child?.length"
          class="new-sidebar-menu__entry new-sidebar-menu__entry--level-2"
          :class="[
            levelTwo.class,
            { 'new-sidebar-menu__entry--active': isEntryActive(levelTwo, 2) },
          ]"
          :to="entryDestination(levelTwo)"
          @click="closeFlyout"
        >
          <span class="new-sidebar-menu__label" :title="levelTwo.title">
            {{ levelTwo.title }}
          </span>
          <LISABadge
            v-if="levelTwo.badge"
            class="new-sidebar-menu__badge"
            :variant="levelTwo.badge.variant ?? 'neutral'"
          >
            {{ levelTwo.badge.text }}
          </LISABadge>
        </RouterLink>
        <div v-else class="new-sidebar-menu__flyout-group">
          <div
            class="new-sidebar-menu__entry new-sidebar-menu__entry--group new-sidebar-menu__entry--level-2"
            :class="[levelTwo.class]"
          >
            {{ levelTwo.title }}
          </div>
          <RouterLink
            v-for="levelThree in levelTwo.child"
            :key="levelThree.href ?? levelThree.title"
            class="new-sidebar-menu__entry new-sidebar-menu__entry--level-3"
            :class="[
              levelThree.class,
              { 'new-sidebar-menu__entry--active': isEntryActive(levelThree, 3) },
            ]"
            :to="entryDestination(levelThree)"
            @click="closeFlyout"
          >
            <span class="new-sidebar-menu__label" :title="levelThree.title">
              {{ levelThree.title }}
            </span>
            <LISABadge
              v-if="levelThree.badge"
              class="new-sidebar-menu__badge"
              :variant="levelThree.badge.variant ?? 'neutral'"
            >
              {{ levelThree.badge.text }}
            </LISABadge>
          </RouterLink>
        </div>
      </template>
    </section>
  </aside>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import {
  RouterLink,
  useRoute,
  useRouter,
  type RouteLocationRaw,
  type RouteLocationResolved,
} from "vue-router";
import { useUserMenu, type MenuEntryType } from "@/functions/useUserMenu";
import { useTranslation } from "@/language/translation";
import { LISABadge, type LISAVariant } from "@fi-solutions/lisa-ui";
import { isLISA3 } from "@/composables/useLISA3";
import { useMenuNotificationBadges } from "@/functions/useMenuNotificationBadges";
type NewSidebarEntry = {
  title: string;
  icon?: string;
  href?: string;
  badge?: {
    text: string;
    class?: string;
    variant?: LISAVariant;
    notification?: boolean;
  };
  child?: NewSidebarEntry[];
  callback?: (event: Event, item: MenuEntryType) => void;
  external?: boolean;
  class?: string;
  menuKey?: string;
};

const emit = defineEmits<{
  openCommandMenu: [event: Event, item: MenuEntryType];
}>();

const { t } = useTranslation();
const route = useRoute();
const router = useRouter();
const isCollapsed = ref(true);
const openEntries = ref(new Set<string>());
const flyoutEntry = ref<NewSidebarEntry>();
const flyoutTop = ref(0);
const flyoutBottom = ref<number>();
const flyoutMaxHeight = ref(0);
const flyoutMaxWidth = ref(0);
const mac = navigator.userAgent.includes("Mac");
const { menu: userMenu } = useUserMenu();
const { counts: notificationCounts } = useMenuNotificationBadges(isLISA3);

function addNotificationBadges(
  entries: NewSidebarEntry[],
  depth = 0,
  ancestorMenuKeys = new Set<string>(),
): NewSidebarEntry[] {
  return entries.map((entry) => {
    const menuKey = entry.menuKey ?? "";
    const count = notificationCounts.value.get(menuKey) ?? 0;
    const duplicatesAncestor = menuKey !== "" && ancestorMenuKeys.has(menuKey);
    const mayShowNotification = (depth === 0 || !entry.child?.length) && !duplicatesAncestor;
    const descendantMenuKeys = new Set(ancestorMenuKeys);
    if (menuKey !== "") {
      descendantMenuKeys.add(menuKey);
    }
    return {
      ...entry,
      badge:
        mayShowNotification && count > 0
          ? { text: count.toString(), variant: "error", notification: true }
          : entry.badge,
      child:
        entry.child == null
          ? undefined
          : addNotificationBadges(entry.child, depth + 1, descendantMenuKeys),
    };
  });
}

const menu = computed<NewSidebarEntry[]>(() => [
  {
    icon: "fas fa-magnifying-glass",
    title: t("global.search"),
    badge: mac ? { text: "⌘ + K" } : undefined,
    callback(event, item) {
      emit("openCommandMenu", event, item);
    },
  },
  ...addNotificationBadges(userMenu.value as NewSidebarEntry[]),
]);

const isMenuReady = computed(() => menu.value.length > 0);

function entryKey(entry: NewSidebarEntry, index: number, parentKey = "root") {
  return `${parentKey}/${entry.href ?? entry.title}-${index}`;
}

function entryDestination(entry: NewSidebarEntry): RouteLocationRaw {
  return entry.href ?? `/redirect/${entry.menuKey ?? entry.title}`;
}

function resolveRedirects(initialRoute: RouteLocationResolved) {
  let resolvedRoute = initialRoute;

  for (let redirectDepth = 0; redirectDepth < 5; redirectDepth += 1) {
    const redirect = resolvedRoute.matched.at(-1)?.redirect;
    if (redirect == null) {
      break;
    }

    const destination = typeof redirect === "function" ? redirect(resolvedRoute, route) : redirect;
    resolvedRoute = router.resolve(destination);
  }

  return resolvedRoute;
}

function isDirectlyActive(entry: NewSidebarEntry) {
  if (entry.external || entry.callback != null) {
    return false;
  }

  const menuRoute = router.resolve(entryDestination(entry));
  const targetRoute = resolveRedirects(menuRoute);
  const targetRecord = targetRoute.matched.at(-1);
  if (targetRecord == null) {
    return targetRoute.path === route.path;
  }

  const normalizedTarget = targetRecord.aliasOf ?? targetRecord;
  const matchesRouteRecord = route.matched.some(
    (record) => (record.aliasOf ?? record) === normalizedTarget,
  );
  const matchesRouteParams = Object.entries(menuRoute.params).every(([key, value]) => {
    const hasConcreteMenuParam = Array.isArray(value)
      ? value.length > 0
      : value != null && value !== "";
    return !hasConcreteMenuParam || JSON.stringify(route.params[key]) === JSON.stringify(value);
  });

  return matchesRouteRecord && matchesRouteParams;
}

function isEntryActive(entry: NewSidebarEntry, level: number): boolean {
  const isIntermediateGroup = level === 2 && Boolean(entry.child?.length);
  return (
    (!isIntermediateGroup && isDirectlyActive(entry)) ||
    (entry.child?.some((child) => isEntryActive(child, level + 1)) ?? false)
  );
}

function isEntryOpen(entry: NewSidebarEntry, index: number, parentKey = "root") {
  return openEntries.value.has(entryKey(entry, index, parentKey));
}

function toggleEntry(key: string) {
  const nextOpenEntries = new Set(openEntries.value);
  if (nextOpenEntries.has(key)) {
    nextOpenEntries.delete(key);
  } else {
    nextOpenEntries.add(key);
  }
  openEntries.value = nextOpenEntries;
}

function openActivePath() {
  const nextOpenEntries = new Set(openEntries.value);

  function addActiveEntries(entries: NewSidebarEntry[], parentKey = "root", level = 1) {
    entries.forEach((entry, index) => {
      if (!entry.child?.length || !isEntryActive(entry, level)) {
        return;
      }

      const key = entryKey(entry, index, parentKey);
      nextOpenEntries.add(key);
      addActiveEntries(entry.child, key, level + 1);
    });
  }

  addActiveEntries(menu.value);
  openEntries.value = nextOpenEntries;
}

function activateEntry(event: Event, entry: NewSidebarEntry, index: number) {
  if (entry.callback != null) {
    entry.callback(event, entry as MenuEntryType);
    return;
  }

  if (entry.child?.length && !isCollapsed.value) {
    toggleEntry(entryKey(entry, index));
  }

  router.push(entryDestination(entry));
}

function activateIntermediateEntry(key: string) {
  toggleEntry(key);
}

function showFlyout(event: MouseEvent, entry: NewSidebarEntry) {
  if (!isCollapsed.value) {
    return;
  }

  if (!entry.child?.length) {
    closeFlyout();
    return;
  }

  const itemElement = event.currentTarget as HTMLElement;
  const itemRect = itemElement.getBoundingClientRect();
  const sidebarRect = itemElement.closest(".new-sidebar-menu")!.getBoundingClientRect();
  const viewportTop = Math.max(sidebarRect.top, 0) + 8;
  const viewportBottom = Math.min(sidebarRect.bottom, window.innerHeight) - 8;
  const opensUpward = itemRect.top + itemRect.height / 2 > (viewportTop + viewportBottom) / 2;
  const anchorTop = Math.max(itemRect.top, viewportTop);
  const anchorBottom = Math.min(itemRect.bottom, viewportBottom);
  const availableHeight = opensUpward
    ? Math.max(anchorBottom - viewportTop, 0)
    : Math.max(viewportBottom - anchorTop, 0);

  flyoutEntry.value = entry;
  flyoutTop.value = anchorTop - sidebarRect.top;
  flyoutBottom.value = opensUpward ? sidebarRect.bottom - anchorBottom : undefined;
  flyoutMaxHeight.value = availableHeight;
  flyoutMaxWidth.value = Math.max(window.innerWidth - sidebarRect.right - 8, 0);
}

function closeFlyout() {
  flyoutEntry.value = undefined;
  flyoutBottom.value = undefined;
}

function toggleSidebar() {
  const willExpand = isCollapsed.value;
  isCollapsed.value = !isCollapsed.value;
  if (willExpand) {
    openActivePath();
  }
  closeFlyout();
}

watch(
  () => route.fullPath,
  () => {
    if (!isCollapsed.value) {
      openActivePath();
    }
    closeFlyout();
  },
);
</script>

<style scoped lang="scss">
.new-sidebar-menu__navigation {
  overflow-x: hidden !important;
}

.new-sidebar-menu {
  --new-sidebar-row-height: 2.75rem;
  --new-sidebar-collapsed-width: 3rem;

  position: relative;
  z-index: 16;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  flex: 0 0 18rem;
  width: 18rem;
  height: 100%;
  color: var(--p-text-color);
  background: var(--p-content-background);
  border-right: 1px solid var(--p-content-border-color);
  transition:
    width 160ms ease,
    flex-basis 160ms ease;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  &--collapsed {
    flex-basis: var(--new-sidebar-collapsed-width);
    width: var(--new-sidebar-collapsed-width);
  }

  &__navigation {
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: thin;
    overscroll-behavior: contain;
  }

  &__list,
  &__submenu {
    padding: 0;
    margin: 0;
    list-style: none;
  }

  &__entry {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    min-width: 0;
    color: inherit;
    font: inherit;
    text-align: left;
    text-decoration: none;
    cursor: pointer;
    background: transparent;
    border: 0;
    transition:
      color 120ms ease,
      background-color 120ms ease;

    &:hover {
      background: var(--p-content-hover-background);
    }

    &--active {
      color: var(--p-primary-color);
      font-weight: 600;
      background: color-mix(in srgb, var(--p-primary-color) 8%, transparent);
      box-shadow: 4px 0 0 var(--p-primary-color) inset;
    }

    &--group {
      cursor: default;
    }

    &--group:hover {
      background: transparent;
    }

    &--level-1 {
      min-height: var(--new-sidebar-row-height);
      padding: 0 0.75rem 0 0;
      font-size: 0.9375rem;
    }

    &--level-2 {
      min-height: 2.5rem;
      padding: 0.35rem 0.875rem 0.35rem 3rem;
      font-size: 0.875rem;
      font-weight: 600;
    }

    &--level-3 {
      min-height: 2.375rem;
      padding: 0.35rem 1rem 0.35rem 4rem;
      font-size: 0.875rem;
    }
  }

  &__icon {
    display: inline-flex;
    flex: 0 0 3rem;
    align-items: center;
    justify-content: center;
    color: var(--p-text-muted-color);
    font-size: 1.5rem;
  }

  &__entry--active &__icon {
    color: var(--p-primary-color);
  }

  &__label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__badge {
    flex: none;
    margin-left: 0.5rem;
    padding: 0.125rem 0.45rem;
    line-height: 1rem;
  }

  &__arrow {
    flex: none;
    margin-left: 0.625rem;
    font-size: 0.75rem;
    transition: transform 140ms ease;

    &--open {
      transform: rotate(90deg);
    }
  }

  &__submenu {
    padding: 0.25rem 0 0.5rem;
    background: color-mix(in srgb, var(--p-text-color) 2%, var(--p-content-background));

    &--level-3 {
      padding-top: 0;
      background: transparent;
    }
  }

  &__toggle {
    display: grid;
    grid-template-rows: 2.75rem 2.75rem;
    align-items: center;
    justify-items: start;
    width: 100%;
    height: 5.5rem;
    padding: 0 0 0 1.45rem;
    color: var(--p-text-color);
    cursor: pointer;
    background: var(--p-content-background);
    border: 0;

    &:hover {
      background: var(--p-content-hover-background);
    }
  }

  &__bug-space {
    display: block;
    width: 2rem;
    height: 2rem;
    visibility: hidden;
  }

  &__collapse-icon {
    font-size: 1.125rem;
  }

  &__flyout {
    position: absolute;
    left: 100%;
    z-index: 20;
    width: 18rem;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
    background: var(--p-content-background);
    border: 1px solid var(--p-content-border-color);
    border-radius: 0 var(--p-border-radius-md) var(--p-border-radius-md) 0;
    box-shadow: var(--p-overlay-popover-shadow);
  }

  &__flyout-title {
    display: flex;
    align-items: center;
    min-height: var(--new-sidebar-row-height);
    padding: 0 1.25rem;
    color: var(--p-primary-color);
    font-weight: 600;
    text-decoration: none;
    border-bottom: 1px solid var(--p-content-border-color);
  }

  &__flyout &__entry--level-2 {
    padding-left: 1.25rem;
  }

  &__flyout &__entry--level-3 {
    padding-left: 2.5rem;
  }

  &__flyout-group + &__flyout-group {
    border-top: 1px solid var(--p-content-border-color);
  }

  &--collapsed &__label,
  &--collapsed &__badge,
  &--collapsed &__arrow {
    display: none;
  }

  &--collapsed &__entry--level-1 > &__badge--notification {
    position: absolute;
    display: inline-flex;
    top: 0.2rem;
    right: 0.15rem;
    min-width: 1.1rem;
    justify-content: center;
    padding: 0.05rem 0.25rem;
    margin: 0;
    font-size: 0.65rem;
    line-height: 0.9rem;
  }

  &--collapsed &__entry--level-1 {
    justify-content: center;
    padding: 0;
  }

  &--collapsed &__icon {
    flex-basis: 2.5rem;
    width: 2.5rem;
  }

  &--collapsed &__toggle {
    justify-items: center;
    padding-left: 0;
  }

  &--collapsed &__flyout &__label {
    display: block;
  }

  &--collapsed &__flyout &__entry &__badge {
    display: inline-flex;
  }
}
</style>
